import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Edit2,
  Check,
  X,
  Trash2,
  RefreshCw,
  Send,
  Paperclip,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { PdfActions } from '../../components/ui/PdfActions/PdfActions';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { useReceiptDetail } from './hooks/useReceiptDetail';
import {
  useApproveReceipt,
  useRejectReceipt,
  useDeleteReceipt,
} from './hooks/useReceiptMutations';
import { SendReceiptModal } from './components/SendReceiptModal';
import { ReceiptAttachmentsModal } from './components/ReceiptAttachmentsModal';
import { endpoints } from '../../api/endpoints';
import { useAuthStore } from '../../store/authStore';
import type { ReceiptStatus } from '../../types/api';
import { PHOTO_SLOT_LABELS } from './photoSlots';
import styles from './ReceiptDetailPage.module.css';

const STATUS_MAP: Record<ReceiptStatus, BadgeVariant> = {
  pending_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  archived: 'muted',
};

const STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ReceiptDetailPage(): JSX.Element {
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const detail = useReceiptDetail(enc_id ?? '');
  const approve = useApproveReceipt(enc_id ?? '');
  const reject = useRejectReceipt(enc_id ?? '');
  const remove = useDeleteReceipt();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);

  if (!enc_id) return <PageLoader message="Loading…" />;
  if (detail.isLoading) return <PageLoader message="Loading receipt…" />;
  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Receipt not found" />
        <Card>
          <p className={styles.notFound}>This receipt could not be loaded.</p>
        </Card>
      </div>
    );
  }

  const r = detail.data;
  // Key-based signatures arrive as a presigned URL with `*_signature`
  // nulled; legacy/offline rows carry an inline data URI. URL wins.
  const sellerSignatureSrc = r.seller_signature_url || r.seller_signature || null;
  const buyerSignatureSrc = r.buyer_signature_url || r.buyer_signature || null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Receipt ${r.unique_id}`}
        subtitle={r.reference ?? undefined}
        actions={
          <div className={styles.actionsRow}>
            <PdfActions
              endpoint={endpoints.receipts.pdf(enc_id)}
              filename={`receipt-${r.unique_id}.pdf`}
            />
            <Button
              variant={r.is_sent ? 'secondary' : 'primary'}
              icon={r.is_sent ? RefreshCw : Send}
              onClick={() => setSendOpen(true)}
            >
              {r.is_sent ? 'Resend' : 'Send'}
            </Button>
            {r.photos.length > 0 ? (
              <Button
                variant="secondary"
                icon={Paperclip}
                onClick={() => setAttachmentsOpen(true)}
                title="View attachments"
              >
                Attachments ({r.photos.length})
              </Button>
            ) : null}
            {isAdmin && (r.status === 'pending_review' || r.status === 'rejected') ? (
              <Button
                variant="secondary"
                icon={Edit2}
                onClick={() => navigate(`/collections/receipts/${encodeURIComponent(enc_id)}/edit`)}
              >
                Edit
              </Button>
            ) : null}
          </div>
        }
      />

      <div className={styles.headerRow}>
        <div className={styles.statusRow}>
          <StatusBadge
            status={r.status}
            statusMap={STATUS_MAP}
            formatLabel={(s) => STATUS_LABELS[s]}
          />
          {r.status === 'pending_review' && isAdmin ? (
            <>
              <Button
                size="sm"
                variant="primary"
                icon={Check}
                loading={approve.isPending}
                onClick={() => approve.mutate()}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="danger"
                icon={X}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          ) : null}
          {isAdmin ? (
            <Button
              size="sm"
              variant="ghost"
              icon={Trash2}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      <div className={styles.grid}>
        <Card title="Vehicle">
          <dl className={styles.fieldList}>
            <span className={styles.fieldLabel}>Registration</span>
            <span className={styles.fieldValue}>{r.car_registration}</span>
            <span className={styles.fieldLabel}>Make / Model</span>
            <span className={styles.fieldValue}>{r.car_make_model}</span>
            <span className={styles.fieldLabel}>Colour</span>
            <span className={styles.fieldValue}>{r.car_color ?? '—'}</span>
            <span className={styles.fieldLabel}>Year</span>
            <span className={styles.fieldValue}>{r.car_year ?? '—'}</span>
            <span className={styles.fieldLabel}>Mileage</span>
            <span className={styles.fieldValue}>{r.car_mileage ?? '—'}</span>
            <span className={styles.fieldLabel}>Chassis</span>
            <span className={styles.fieldValue}>{r.chassis ?? '—'}</span>
            <span className={styles.fieldLabel}>MOT</span>
            <span className={styles.fieldValue}>{r.has_mot ? 'Yes' : 'No'}</span>
            <span className={styles.fieldLabel}>Keys</span>
            <span className={styles.fieldValue}>{r.has_keys ? 'Yes' : 'No'}</span>
            <span className={styles.fieldLabel}>Logbook</span>
            <span className={styles.fieldValue}>{r.has_logbook ? 'Yes' : 'No'}</span>
          </dl>
        </Card>

        <Card title="Customer & payment">
          <dl className={styles.fieldList}>
            <span className={styles.fieldLabel}>Name</span>
            <span className={styles.fieldValue}>{r.customer_name}</span>
            <span className={styles.fieldLabel}>Phone</span>
            <span className={styles.fieldValue}>{r.customer_phone}</span>
            <span className={styles.fieldLabel}>Email</span>
            <span className={styles.fieldValue}>{r.customer_email ?? '—'}</span>
            <span className={styles.fieldLabel}>Address</span>
            <span className={styles.fieldValue}>{r.effective_address}</span>
            <span className={styles.fieldLabel}>Date</span>
            <span className={styles.fieldValue}>{formatDate(r.receipt_date)}</span>
            <span className={styles.fieldLabel}>Driver</span>
            <span className={styles.fieldValue}>{r.driver_name ?? '—'}</span>
            <span className={styles.fieldLabel}>Payment</span>
            <span className={styles.fieldValue}>{r.payment_mode_name ?? '—'}</span>
            <span className={styles.fieldLabel}>Cheque Number</span>
            <span className={styles.fieldValue}>{r.cheque_number ?? '—'}</span>
            <span className={styles.fieldLabel}>Account Name</span>
            <span className={styles.fieldValue}>{r.account_name ?? '—'}</span>
            <span className={styles.fieldLabel}>Account Number</span>
            <span className={styles.fieldValue}>{r.account_number ?? '—'}</span>
            <span className={styles.fieldLabel}>Sort Code</span>
            <span className={styles.fieldValue}>{r.sort_code ?? '—'}</span>
            <span className={styles.fieldLabel}>Amount</span>
            <span className={styles.fieldValue}>{formatMoney(r.amount)}</span>
          </dl>
        </Card>
      </div>

      {r.notes ? (
        <Card title="Notes">
          <p>{r.notes}</p>
        </Card>
      ) : null}

      <Card title={`Photos (${r.photos.length})`}>
        {r.photos.length === 0 ? (
          <p className={styles.notFound}>No photos uploaded.</p>
        ) : (
          <div className={styles.photoGrid}>
            {r.photos.map((p) => (
              <div key={p.enc_id} className={styles.photo}>
                {p.presigned_url ? (
                  <img
                    src={p.presigned_url}
                    alt={PHOTO_SLOT_LABELS[p.caption ?? ''] ?? p.caption ?? 'Receipt photo'}
                    className={styles.photoImg}
                  />
                ) : (
                  <span className={styles.photoPlaceholder}>Preview unavailable</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className={styles.grid}>
        <Card title="Seller signature">
          {sellerSignatureSrc ? (
            <div className={styles.signatureBlock}>
              <img
                src={sellerSignatureSrc}
                alt="Seller signature"
                className={styles.signatureImg}
              />
            </div>
          ) : (
            <p className={styles.notFound}>Not captured</p>
          )}
        </Card>
        <Card title="Buyer signature">
          {buyerSignatureSrc ? (
            <div className={styles.signatureBlock}>
              <img
                src={buyerSignatureSrc}
                alt="Buyer signature"
                className={styles.signatureImg}
              />
            </div>
          ) : (
            <p className={styles.notFound}>Not captured</p>
          )}
        </Card>
      </div>

      {r.status === 'rejected' && r.rejection_reason ? (
        <Card title="Rejection reason">
          <p>{r.rejection_reason}</p>
        </Card>
      ) : null}

      <ConfirmModal
        open={rejectOpen}
        title="Reject receipt?"
        message={
          <div>
            <p>Provide a reason — this will be recorded on the receipt.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{ width: '100%', marginTop: '0.5rem' }}
              aria-label="Rejection reason"
            />
          </div>
        }
        confirmLabel="Reject"
        cancelLabel="Cancel"
        variant="danger"
        loading={reject.isPending}
        onCancel={() => {
          setRejectOpen(false);
          setRejectReason('');
        }}
        onConfirm={() => {
          if (!rejectReason.trim()) return;
          reject.mutate(
            { reason: rejectReason.trim() },
            {
              onSuccess: () => {
                setRejectOpen(false);
                setRejectReason('');
              },
            },
          );
        }}
      />

      <SendReceiptModal
        open={sendOpen}
        encId={enc_id}
        isResend={r.is_sent}
        customerName={r.customer_name}
        customerEmail={r.customer_email}
        customerPhone={r.customer_phone}
        onClose={() => setSendOpen(false)}
      />

      <ReceiptAttachmentsModal
        open={attachmentsOpen}
        encId={enc_id}
        initialPhotos={r.photos}
        onClose={() => setAttachmentsOpen(false)}
      />

      <ConfirmModal
        open={deleteOpen}
        title="Delete receipt?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          remove.mutate(enc_id, {
            onSuccess: () => {
              setDeleteOpen(false);
              navigate('/collections/receipts');
            },
          });
        }}
      />
    </div>
  );
}
